import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { api } from '../../helpers/apiUtils';
import {
  formatBerlin,
  renderEtkp,
  renderEur,
  renderInt,
  renderRate,
  statusChipColor,
} from './shared';
import HistoryKpiCards from './HistoryKpiCards';
import SamHtmlPreviewDialog, { SamHtmlPreviewData } from './SamHtmlPreviewDialog';
import AngeboteWarmupSection from './AngeboteWarmupSection';

interface PlannedRow {
  id: number;
  send_date: string;
  sam_id: number;
  product_id: number;
  customer_id: string;
  status: string;
  it_campaign_id: number | null;
  campaign_title: string;
  dup_send: number;
  inactive_send: number;
  duplicate_customer_ids: string | null;
  product_name: string | null;
  sam_subject: string | null;
}

interface HistoryRow extends PlannedRow {
  recipients: number | null;
  unique_opens: number | null;
  unique_clicks: number | null;
  unsubscribed: number | null;
  error_message: string | null;
  // Attribution: revenue from verlag_affiliates JOIN by REGEXP_SUBSTR(affiliate,
  // 'FT-ENL-SAM-M-ID-(N)'); sois + leads from LocalDB process+process_details.
  // NULL = no data yet (typical for sends within the last ~24h before coreg
  // tracking catches up); 0 = sent but produced nothing.
  revenue: string | number | null;
  leads: number | null;
  sois: number | null;
}

const FtTab: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [planned, setPlanned] = useState<PlannedRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [runSendDate, setRunSendDate] = useState('');
  const [running, setRunning] = useState(false);

  const [preview, setPreview] = useState<SamHtmlPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // History filters — all in-memory; the `days` selector controls the backend
  // window and these filters slice the response.
  const [filterCustomers, setFilterCustomers] = useState<string[]>([]);
  const [filterSendType, setFilterSendType] = useState<'all' | 'main' | 'dup'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'cancelled' | 'failed'>('all');

  const customerOptions = useMemo(
    () => Array.from(new Set(history.map((r) => r.customer_id).filter(Boolean) as string[])).sort(),
    [history],
  );

  const filteredHistory = useMemo(() => {
    return history.filter((r) => {
      if (
        filterCustomers.length > 0 &&
        (!r.customer_id || !filterCustomers.includes(r.customer_id))
      ) {
        return false;
      }
      if (filterSendType === 'main' && r.dup_send) return false;
      if (filterSendType === 'dup' && !r.dup_send) return false;
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      return true;
    });
  }, [history, filterCustomers, filterSendType, filterStatus]);

  const loadPlanned = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/ft-sams/planned');
      setPlanned(data?.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/ft-sams/history?days=60');
      setHistory(data?.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlanned();
  }, [loadPlanned]);

  useEffect(() => {
    if (tab === 1) loadHistory();
  }, [tab, loadHistory]);

  const handleCancel = async (id: number) => {
    if (!window.confirm(`FT SAM-Versand #${id} stornieren? Iterable-Campaign wird gelöscht.`)) {
      return;
    }
    setError(null);
    setInfo(null);
    try {
      const { data } = await api.post(`/api/adserver/ft-sams/cancel/${id}`, {});
      if (data?.success) {
        setInfo(`Versand #${id} storniert.`);
        loadPlanned();
      } else {
        setError(data?.error ?? 'Cancel fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handlePreview = async (id: number) => {
    setError(null);
    try {
      const { data } = await api.get(`/api/adserver/ft-sams/preview/${id}`);
      if (data?.success) {
        setPreview(data.data as SamHtmlPreviewData);
        setPreviewOpen(true);
      } else {
        setError(data?.error ?? 'Preview fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleRunNow = async () => {
    setError(null);
    setInfo(null);
    setRunning(true);
    try {
      const body: { sendDate?: string } = {};
      if (runSendDate) body.sendDate = runSendDate;
      const { data } = await api.post('/api/adserver/ft-sams/run-now', body);
      if (data?.success && data.summary) {
        const s = data.summary;
        setInfo(
          `Pipeline-Lauf für ${s.sendDate}: total=${s.total} scheduled=${s.scheduled} skipped=${s.skipped} failed=${s.failed}`,
        );
        loadPlanned();
      } else {
        setError(data?.error ?? 'Run-Now fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <AngeboteWarmupSection />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Geplante Versendungen (7 Tage)" />
        <Tab label="Historie (60 Tage)" />
        <Tab label="Manueller Lauf" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      'Sendezeit',
                      'Typ',
                      'Status',
                      'SAM',
                      'Produkt',
                      'Kunde',
                      'Iterable',
                      'Aktion',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {planned.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Keine geplanten FT-SAMs in den nächsten 7 Tagen.
                      </TableCell>
                    </TableRow>
                  )}
                  {planned.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatBerlin(row.send_date)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.dup_send ? 'Dup' : 'Main'}
                          color={row.dup_send ? 'default' : 'primary'}
                        />
                        {row.inactive_send ? (
                          <Chip size="small" label="Inaktiv" sx={{ ml: 0.5 }} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.status} color={statusChipColor(row.status)} />
                      </TableCell>
                      <TableCell>
                        #{row.sam_id}
                        {row.sam_subject ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.sam_subject}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        #{row.product_id}
                        {row.product_name ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.product_name}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row.customer_id}
                        {row.duplicate_customer_ids ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            (dup: {row.duplicate_customer_ids})
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.it_campaign_id ?? '–'}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => handlePreview(row.id)}>
                          Vorschau
                        </Button>
                        {row.status === 'scheduled' && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleCancel(row.id)}
                            sx={{ ml: 1 }}
                          >
                            Stornieren
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 2, p: 1.5, backgroundColor: '#ffffff', borderRadius: 1 }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="ft-customer-filter-label">Kunde</InputLabel>
                  <Select
                    labelId="ft-customer-filter-label"
                    multiple
                    value={filterCustomers}
                    onChange={(e) =>
                      setFilterCustomers(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : (e.target.value as string[]),
                      )
                    }
                    input={<OutlinedInput label="Kunde" />}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {customerOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filterSendType}
                  onChange={(_e, v) => v && setFilterSendType(v)}
                  aria-label="Send-Typ"
                >
                  <ToggleButton value="all">Alle</ToggleButton>
                  <ToggleButton value="main">Main</ToggleButton>
                  <ToggleButton value="dup">Dup</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filterStatus}
                  onChange={(_e, v) => v && setFilterStatus(v)}
                  aria-label="Status"
                >
                  <ToggleButton value="all">Alle</ToggleButton>
                  <ToggleButton value="sent">Sent</ToggleButton>
                  <ToggleButton value="cancelled">Cancelled</ToggleButton>
                  <ToggleButton value="failed">Failed</ToggleButton>
                </ToggleButtonGroup>
                {(filterCustomers.length > 0 ||
                  filterSendType !== 'all' ||
                  filterStatus !== 'all') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setFilterCustomers([]);
                      setFilterSendType('all');
                      setFilterStatus('all');
                    }}
                  >
                    Zurücksetzen
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {filteredHistory.length} / {history.length} Sendungen
                </Typography>
              </Stack>
              <HistoryKpiCards rows={filteredHistory} />
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        'Sendezeit',
                        'Typ',
                        'Status',
                        'SAM',
                        'Kunde',
                        'Empf.',
                        'Opens',
                        'Clicks',
                        'SOI',
                        'Leads',
                        'Revenue',
                        'ETKP',
                        'Unsubs',
                        'Aktion',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={14} align="center">
                          {history.length === 0 ? 'Keine Historie.' : 'Keine Treffer für Filter.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredHistory.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{formatBerlin(row.send_date)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.dup_send ? 'Dup' : 'Main'}
                            color={row.dup_send ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={statusChipColor(row.status)}
                          />
                          {row.error_message ? (
                            <Typography
                              variant="caption"
                              display="block"
                              color="error.main"
                              sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {row.error_message}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          #{row.sam_id}{' '}
                          <Typography variant="caption" color="text.secondary">
                            {row.sam_subject ?? ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.customer_id}</TableCell>
                        <TableCell align="right">{renderInt(row.recipients)}</TableCell>
                        <TableCell align="right">
                          {renderInt(row.unique_opens)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unique_opens, row.recipients)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {renderInt(row.unique_clicks)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unique_clicks, row.recipients)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{renderInt(row.sois)}</TableCell>
                        <TableCell align="right">
                          {renderInt(row.leads)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.leads, row.recipients, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{renderEur(row.revenue)}</TableCell>
                        <TableCell align="right">
                          {renderEtkp(row.revenue, row.recipients)}
                        </TableCell>
                        <TableCell align="right">
                          {renderInt(row.unsubscribed)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unsubscribed, row.recipients, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handlePreview(row.id)}>
                            Vorschau
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box maxWidth="600px">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Manueller Trigger der FT SAM Pipeline. Plant Main + (optional) Dup für das gewählte
            Sendedatum (Default: morgen 06:00). SAM-Auswahl erfolgt automatisch über mc_sams_scores
            (FTWP-Parität).
          </Typography>
          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Sendedatum"
              InputLabelProps={{ shrink: true }}
              value={runSendDate}
              onChange={(e) => setRunSendDate(e.target.value)}
            />
            <Button variant="contained" onClick={handleRunNow} disabled={running}>
              {running ? 'läuft…' : 'Jetzt ausführen'}
            </Button>
          </Box>
        </Box>
      )}

      <SamHtmlPreviewDialog
        open={previewOpen}
        preview={preview}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default FtTab;
